import React, { useState } from "react";
import { Outlet, Route, Routes, useLocation, useNavigate } from "react-router-dom";
import DemoNavbar from "components/Navbars/DemoNavbar.js";
import Footer from "components/Footer/Footer.js";
import Sidebar from "components/Sidebar/Sidebar.js";
import routes from "routes.js";
import { AppointmentRoutes } from "views/Appointment";
import { useDispatch, useSelector } from "react-redux";
import { GHOST_ADMIN_KEY, returnFromGhost, userSelector } from "slices/userSlice";

export function usePermissions() {
  const { user } = useSelector(userSelector);
  function checkPermission(value) {
    if (user?.role === "admin" || user?.role === "super_admin") return true;
    return !!user?.permissions?.some(i => i.name === value);
  }

  // For the few places that must be admin-only rather than permission-gated (a subuser can hold
  // a permission, but is not an admin) — e.g. overriding a case's derived Service Type.
  const isAdmin = user?.role === "admin" || user?.role === "super_admin";

  return { checkPermission, isAdmin };
}


// Shown only while an admin is ghost-logged-in as a SUB USER (the admin session is
// stashed under GHOST_ADMIN_KEY) — one click swaps the session back, no re-login.
function AdminGhostBanner() {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { user } = useSelector(userSelector);

  if (user?.role !== 'subuser' || !localStorage.getItem(GHOST_ADMIN_KEY)) return null;

  const handleReturn = () => {
    dispatch(returnFromGhost((success) => {
      if (success) navigate('/permissions/sub-users');
    }));
  };

  return (
    <div style={{
      background: '#5e72e4', color: '#fff', padding: '8px 16px', textAlign: 'center',
      position: 'sticky', top: 0, zIndex: 1050, fontSize: '14px',
    }}>
      Ghost mode — you are viewing the dashboard as <strong>{user?.user?.name}</strong> (sub user).
      <button type="button" onClick={handleReturn} style={{
        marginLeft: '12px', background: '#fff', color: '#5e72e4', border: 'none',
        borderRadius: '4px', padding: '3px 12px', cursor: 'pointer', fontWeight: 600,
      }}>
        Return to Admin
      </button>
    </div>
  );
}

function Dashboard(props) {
  const dispatch = useDispatch();
  const { user, logout } = useSelector(userSelector);
  const [isSidebarOpen, setIsSidebarOpen] = useState(false); // Sidebar toggle state
  const mainPanel = React.useRef();
  const location = useLocation();

  React.useEffect(() => {
    mainPanel.current.scrollTop = 0;
    document.scrollingElement.scrollTop = 0;
  }, [location]);

  const toggleSidebar = () => {
    setIsSidebarOpen(!isSidebarOpen);
  };
  let allowedUrls
  if (user?.permissions) {
    allowedUrls = user?.permissions?.map(i => i.url)?.filter(url => url)?.map(url => (url.startsWith("/") ? url?.slice(1) : url));
  }
  else {
    localStorage.removeItem("persist:root");
    setTimeout(() => {
      window.location.replace("/login")
    }, 200);
  }
  const filteredRoutes = routes.filter(prop => {
    const routePath = prop?.path === "" ? "dashboard" : prop?.path;
    return user?.role === "admin" || user?.role === "super_admin" || allowedUrls?.some(url => routePath.startsWith(url));
  });

  return (
    <div className="wrapper">
      <Sidebar
        {...props}
        user={user}
        allowedUrls={allowedUrls}
        routes={filteredRoutes}
        isSidebarOpen={isSidebarOpen}
        toggleSidebar={toggleSidebar}
      />
      <div className={`main-panel ${isSidebarOpen ? "collaped" : ""}`} ref={mainPanel}>
        <AdminGhostBanner />
        <DemoNavbar {...props} toggleSidebar={toggleSidebar} />
        <Routes>
          {filteredRoutes.map((prop, key) => (
            prop?.path !== "appointments" && (
              <Route path={prop.path} element={prop.component} key={key} />
            )
          ))}
          <Route path="/appointments/*" element={<AppointmentRoutes />} />
        </Routes>
        <Footer fluid />
      </div>
      <Outlet />
    </div>
  );
}

export default Dashboard;
